import { type RefObject, useMemo, useRef, useState } from 'react';

import type { SentimentChartData } from '@/components/Chart/SentimentTrendChart/SentimentTrendChart';
import AMP_KEYS from '@/configs/amp-track-keys';
import STORAGE_KEYS from '@/configs/local-storage-keys';
import useWorkspaceStorage from '@/hooks/pure/useWorkspaceStorage';
import { scrollToTargetAdjusted } from '@/utils/dom';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';
import { track } from '@/utils/track/track';

type Props = {
  isFullView: boolean;
  scrollSelector?: string;
  chat_messages?: ChatMessageT[];
  blocks: FeedbackBlockT[];
  sentences: FeedbackSentenceT[];
  chat_title?: string;
};

export type ContentBodyConfigs = {
  chatRef: RefObject<HTMLDivElement>;
  showCustomerOnly: boolean;
  selectedChat: string | null;
  chat_messages: ChatMessageWithSentence[];
  chat_title: string;
  sentimentChartData: SentimentChartData[];
  onChartCirlceClick: (alias: string) => void;
  setShowCustomerOnly: (status: boolean) => void;
};

const useContentBodyConfig = (props: Props): ContentBodyConfigs => {
  let delayTimer = useRef<NodeJS.Timer | null>(null);
  let durationTimer = useRef<NodeJS.Timer | null>(null);
  const [showCustomerOnly, setShowCustomerOnly] = useWorkspaceStorage(
    STORAGE_KEYS.FEEDBACK_DETAIL_SHOW_CONTACT_FEED_ONLY_KEY,
    false,
  );
  const ref = useRef<HTMLDivElement>(null);

  const [selectedChat, setSelectedChat] = useState<string | null>(null);

  const onChartSentenceClick = (alias: string) => {
    if (!props.scrollSelector) return;
    const target = document.querySelector(`[data-chat-id="${alias}"]`);
    if (!target) return;
    const scrollTarget = document.querySelector(props.scrollSelector);
    if (!scrollTarget) return;
    if (delayTimer.current) {
      clearTimeout(delayTimer.current);
    }
    if (durationTimer.current) {
      clearTimeout(durationTimer.current);
    }

    // Track block filter apply event (sentiment chart click)
    track(AMP_KEYS.FEEDBACK_DETAIL_BLOCK_FILTER_APPLY, {
      feedback_alias:
        props.scrollSelector?.match(/feedback-detail-modal-(.*)/)?.[1] ||
        'unknown',
      block_count: 1,
      filter_type: 'sentiment_chart_click',
    });

    const offset = 200;
    const delay = 200;

    delayTimer.current = setTimeout(() => {
      setSelectedChat(alias);
      scrollToTargetAdjusted(target, scrollTarget, offset);
    }, delay);

    durationTimer.current = setTimeout(() => {
      setSelectedChat(null);
    }, 3000);
  };

  const processedChatMessages = useMemo(() => {
    if (!props.chat_messages) {
      return [];
    }
    const chatMessages = [] as ChatMessageWithSentence[];
    let beforeTimestamp = '';
    for (const chat of props.chat_messages) {
      const timestamp = getTimezoneDayjs(chat.created_at).format(
        DATE_FORMAT.readable,
      );
      const sentence = props.sentences.find(
        (s) => s.source_id === chat.source_id,
      );

      if (timestamp !== beforeTimestamp) {
        chatMessages.push({
          source_id: `${chat.source_id}-timestamp`,
          author: null,
          type: 'time',
          secret: false,
          created_at: chat.created_at,
          text: timestamp,
        });
        beforeTimestamp = timestamp;
      }
      chatMessages.push({
        ...chat,
        sentence: sentence,
      });
    }
    return chatMessages;
  }, [props.chat_messages, props.sentences]);

  const sentimentChartData = useMemo(() => {
    return props.blocks.reduce((acc, block) => {
      const targetSourceId = block.locations[0].message_source_id;
      if (!targetSourceId) return acc;
      const message = processedChatMessages.find(
        (m) => m.source_id === targetSourceId,
      );
      if (!message) return acc;
      const item = {
        alias: targetSourceId,
        sentiment: block.sentiment,
        date: message?.created_at,
        primary: false,
      };
      acc.push(item);
      return acc;
    }, [] as SentimentChartData[]);
  }, [processedChatMessages, props.blocks]);

  const handleShowCustomerOnlyChange = (status: boolean) => {
    // Track block filter apply event (show customer only toggle)
    if (props.scrollSelector) {
      track(AMP_KEYS.FEEDBACK_DETAIL_BLOCK_FILTER_APPLY, {
        feedback_alias:
          props.scrollSelector?.match(/feedback-detail-modal-(.*)/)?.[1] ||
          'unknown',
        block_count: processedChatMessages.filter((m) => m.author?.type === 3)
          .length,
        filter_type: 'customer_only_toggle',
      });
    }
    setShowCustomerOnly(status);
  };

  return {
    chatRef: ref,
    showCustomerOnly,
    selectedChat,
    chat_messages: processedChatMessages,
    chat_title: props.chat_title || '',
    sentimentChartData,
    onChartCirlceClick: onChartSentenceClick,
    setShowCustomerOnly: handleShowCustomerOnlyChange,
  };
};

export default useContentBodyConfig;
